import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { BRACKET_FIELD_TUTORIAL_IMAGES } from '../lib/bracketFieldTutorialImages'

/**
 * Step-by-step tutorial modal (bracket fields). Lightweight carousel, no external libs.
 * @param {{ onClose: () => void, t: (key: string) => string }} props
 */
export function HelpTutorialModal({ onClose, t }) {
  const [currentStep, setCurrentStep] = useState(0)
  const total = BRACKET_FIELD_TUTORIAL_IMAGES.length
  const last = total - 1

  const steps = useMemo(
    () =>
      BRACKET_FIELD_TUTORIAL_IMAGES.map((image, i) => ({
        image,
        title: t(`app.bracketTutorial.step${i + 1}Title`),
        description: t(`app.bracketTutorial.step${i + 1}Desc`),
      })),
    [t],
  )

  const step = steps[currentStep]

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const goPrev = useCallback(() => {
    setCurrentStep((s) => Math.max(0, s - 1))
  }, [])

  const goNext = useCallback(() => {
    setCurrentStep((s) => Math.min(last, s + 1))
  }, [last])

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className="bracket-tutorial-modal-backdrop"
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        className="bracket-tutorial-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bracket-tutorial-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bracket-tutorial-modal__header">
          <h2 id="bracket-tutorial-modal-title" className="bracket-tutorial-modal__title">
            {t('app.bracketTutorial.modalTitle')}
          </h2>
          <button
            type="button"
            className="bracket-tutorial-modal__close ghost-button"
            onClick={onClose}
            aria-label={t('modal.close')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="bracket-tutorial-modal__slide" key={currentStep}>
          <h3 className="bracket-tutorial-modal__step-title">{step.title}</h3>
          <p className="bracket-tutorial-modal__step-desc">{step.description}</p>
          <div className="bracket-tutorial-modal__image-wrap">
            <img
              src={step.image}
              alt=""
              className="bracket-tutorial-modal__image"
              decoding="async"
            />
          </div>
        </div>

        <div className="bracket-tutorial-modal__footer">
          <button
            type="button"
            className="ghost-button bracket-tutorial-modal__nav-btn"
            onClick={goPrev}
            disabled={currentStep === 0}
            aria-label={t('app.bracketTutorial.prev')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className="bracket-tutorial-modal__counter" aria-live="polite">
            {currentStep + 1} / {total}
          </span>
          <button
            type="button"
            className="ghost-button bracket-tutorial-modal__nav-btn"
            onClick={goNext}
            disabled={currentStep === last}
            aria-label={t('app.bracketTutorial.next')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
